"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DateTimeBaseField = exports.dateTimeFormat = exports.getDateTimeFormat = void 0;
const dayjs_1 = __importDefault(require("dayjs"));
require("dayjs/locale/zh-cn");
require("dayjs/locale/zh-hk");
require("dayjs/locale/zh-tw");
const timezone_1 = __importDefault(require("dayjs/plugin/timezone"));
// timezone
const utc_1 = __importDefault(require("dayjs/plugin/utc"));
const i18n_1 = require("../../exports/i18n");
const lodash_1 = require("lodash");
const utils_1 = require("../../model/utils");
const field_types_1 = require("../../types/field_types");
const view_types_1 = require("../../types/view_types");
const utils_2 = require("../../utils");
const field_1 = require("./field");
const stat_1 = require("./stat");
const config_1 = require("../../config");
const joi_1 = __importDefault(require("joi"));
const model_1 = require("../../model");
const env_1 = require("../../utils/env");
const selectors_1 = require("../../exports/store/selectors");
const patchDayjsTimezone = (timezone) => {
    // The original version of the functions `getDateTimeFormat` and `tz` comes from
    // https://github.com/iamkun/dayjs/blob/dev/src/plugin/timezone/index.js
    // which is published under the MIT license. See https://github.com/iamkun/dayjs/blob/dev/LICENSE for more information.
    const dtfCache = {};
    const getDateTimeFormat = (timezone, options = {}) => {
        const timeZoneName = options.timeZoneName || 'short';
        const key = `${timezone}|${timeZoneName}`;
        let dtf = dtfCache[key];
        if (!dtf) {
            dtf = new Intl.DateTimeFormat('en-US', {
                // hour12: false,
                hour12: true,
                timeZone: timezone,
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                // timeZoneName
            });
            dtfCache[key] = dtf;
        }
        return dtf;
    };
    const MS = 'millisecond';
    const MIN = 'minute';
    return (o, c, d) => {
        let defaultTimezone;
        timezone(o, c, d);
        // The following function integrates a performance tuning from https://github.com/iamkun/dayjs/issues/1236#issuecomment-1262907180
        c.prototype.tz = function (timezone = defaultTimezone, keepLocalTime = undefined) {
            const oldOffset = this.utcOffset();
            const date = this.toDate();
            const target = getDateTimeFormat(timezone).format(date);
            const diff = Math.round((+date - +new Date(target)) / 1000 / 60);
            let ins = d(target).$set(MS, this.$ms)
                .utcOffset((-Math.round(date.getTimezoneOffset() / 15) * 15) - diff, true);
            if (keepLocalTime) {
                const newOffset = ins.utcOffset();
                ins = ins.add(oldOffset - newOffset, MIN);
            }
            ins.$x.$timezone = timezone;
            return ins;
        };
        const setDefaultTimezone = d.tz.setDefault;
        d.tz.setDefault = (timezone) => {
            setDefaultTimezone(timezone);
            defaultTimezone = timezone;
        };
    };
};
// plugin before import, prevent circular import
dayjs_1.default.extend(utc_1.default);
dayjs_1.default.extend(patchDayjsTimezone(timezone_1.default));
const DEFAULT_DATETIME_PROPS = {
    dateFormat: field_types_1.DateFormat['YYYY/MM/DD'],
    timeFormat: field_types_1.TimeFormat['HH:mm'],
    includeTime: false,
    timeZone: (0, config_1.getTimeZone)(),
};
const getDateTimeFormat = (props) => {
    const dateFormat = field_types_1.DateFormat[props.dateFormat || DEFAULT_DATETIME_PROPS.dateFormat];
    const timeFormat = field_types_1.TimeFormat[props.timeFormat || DEFAULT_DATETIME_PROPS.timeFormat];
    return dateFormat + (props.includeTime ? ' ' + timeFormat : '');
};
exports.getDateTimeFormat = getDateTimeFormat;
const dateTimeFormat = (timestamp, props, userTimeZone) => {
    if (!timestamp) {
        return null;
    }
    props = props || DEFAULT_DATETIME_PROPS;
    const dateFormat = field_types_1.DateFormat[props.dateFormat || DEFAULT_DATETIME_PROPS.dateFormat];
    const timeFormat = field_types_1.TimeFormat[props.timeFormat || DEFAULT_DATETIME_PROPS.timeFormat];
    let format = dateFormat + (props.includeTime ? ' ' + timeFormat : '');
    if (props.includeTime && timeFormat === 'hh:mm') {
        const locale = (0, i18n_1.getLanguage)();
        const formatLocale = locale.toLowerCase().split('_').join('-');
        dayjs_1.default.locale(formatLocale);
        format += ' a';
    }
    let timeZone = props.timeZone || userTimeZone;
    if ((0, env_1.isServer)()) {
        timeZone = timeZone || model_1.DEFAULT_TIME_ZONE;
    }
    try {
        if (props.includeTimeZone) {
            timeZone = timeZone || DEFAULT_DATETIME_PROPS.timeZone;
            const abbr = (0, config_1.getTimeZoneAbbrByUtc)(timeZone);
            return `${(0, dayjs_1.default)(Number(timestamp)).tz(timeZone).format(format)} (${abbr})`;
        }
        if (!props.includeTimeZone && timeZone) {
            return (0, dayjs_1.default)(Number(timestamp)).tz(timeZone).format(format);
        }
    }
    catch (e) {
        if (e instanceof RangeError) {
            return 'Invalid Date';
        }
        throw e;
    }
    return (0, dayjs_1.default)(Number(timestamp)).format(format);
};
exports.dateTimeFormat = dateTimeFormat;
const withTimeZone = (date, timeZone) => timeZone ? date.tz(timeZone) : date;
class DateTimeBaseField extends field_1.Field {
    constructor(field, state) {
        super(field, state);
        this.field = field;
        this.state = state;
    }
    showFOperatorDesc(type) {
        return DateTimeBaseField.FOperatorDescMap[type];
    }
    get apiMetaProperty() {
        const res = {
            format: (0, exports.getDateTimeFormat)(this.field.property),
            includeTime: this.field.property.includeTime,
            timeZone: this.field.property.timeZone,
            includeTimeZone: this.field.property.includeTimeZone
        };
        if (this.field.property.autoFill) {
            res.autoFill = true;
        }
        return res;
    }
    get openValueJsonSchema() {
        return {
            type: 'string',
            title: this.field.name,
        };
    }
    get statTypeList() {
        return DateTimeBaseField._statTypeList;
    }
    get basicValueType() {
        return field_types_1.BasicValueType.DateTime;
    }
    get acceptFilterOperators() {
        return DateTimeBaseField._acceptFilterOperators;
    }
    get dateFormat() {
        return field_types_1.DateFormat[this.field.property.dateFormat];
    }
    get timeFormat() {
        return field_types_1.TimeFormat[this.field.property.timeFormat];
    }
    get mergeFormat() {
        return this.dateFormat + (this.field.property.includeTime ? ' ' + this.timeFormat : '');
    }
    static _validate(value) {
        return (0, lodash_1.isNumber)(value) && !Number.isNaN(value);
    }
    validate(value) {
        return DateTimeBaseField._validate(value);
    }
    static _defaultValueForCondition(condition) {
        if (condition.operator !== view_types_1.FOperator.Is) {
            return null;
        }
        const { value } = condition;
        if (!value || value.length === 0) {
            return null;
        }
        const duration = value[0];
        if (duration === view_types_1.FilterDuration.ExactDate) {
            const timestamp = value[1];
            if (DateTimeBaseField._validate(timestamp)) {
                return timestamp;
            }
            return null;
        }
        if (duration === view_types_1.FilterDuration.Today) {
            const today = (0, utils_2.getToday)();
            return today.getTime();
        }
        if (duration === view_types_1.FilterDuration.Tomorrow) {
            const day = (0, utils_2.getToday)();
            day.setDate(day.getDate() + 1);
            return day.getTime();
        }
        if (duration === view_types_1.FilterDuration.Yesterday) {
            const day = (0, utils_2.getToday)();
            day.setDate(day.getDate() - 1);
            return day.getTime();
        }
        return null;
    }
    defaultValueForCondition(condition) {
        return DateTimeBaseField._defaultValueForCondition(condition);
    }
    defaultValue() {
        return null;
    }
    static _compare(cv1, cv2, property, onlyCompareOriginValue) {
        if (cv1 === cv2) {
            return 0;
        }
        if (cv1 === null) {
            return -1;
        }
        if (cv2 === null) {
            return 1;
        }
        const dateFormat = field_types_1.DateFormat[(property === null || property === void 0 ? void 0 : property.dateFormat) || DEFAULT_DATETIME_PROPS.dateFormat];
        const hasYear = dateFormat.includes('YYYY');
        // In the case of sorting and including the year, just use the original timestamp to compare
        if (onlyCompareOriginValue && hasYear) {
            return cv1 > cv2 ? 1 : -1;
        }
        const dateTimeStr1 = (0, exports.dateTimeFormat)(cv1, property);
        const dateTimeStr2 = (0, exports.dateTimeFormat)(cv2, property);
        // The product requirement is to use the displayed value (that is, the value seen by the cell) to be sorted uniformly.
        // In theory, all of them can be compared using str.
        // But there is a variant of the year-month-day format: day-month-year,
        // if you use str to compare the order at this time, there will be an error,
        // So the format containing the year, month and day uses timestamp comparison uniformly
        if (hasYear) {
            return dateTimeStr1 === dateTimeStr2 ?
                0 : (cv1 > cv2 ? 1 : -1);
        }
        // Fill in a uniform year, then compare the timestamps
        const timestamp1 = (0, dayjs_1.default)(cv1).year(2000).valueOf();
        const timestamp2 = (0, dayjs_1.default)(cv2).year(2000).valueOf();
        return dateTimeStr1 === dateTimeStr2 ?
            0 : (timestamp1 > timestamp2 ? 1 : -1);
    }
    compare(cellValue1, cellValue2, onlyCompareOriginValue) {
        return DateTimeBaseField._compare(cellValue1, cellValue2, this.field.property, onlyCompareOriginValue);
    }
    eq(cv1, cv2) {
        return (0, lodash_1.isEqual)((0, exports.dateTimeFormat)(cv1, this.field.property), (0, exports.dateTimeFormat)(cv2, this.field.property));
    }
    cellValueToString(cellValue) {
        return (0, exports.dateTimeFormat)(cellValue, this.field.property, (0, selectors_1.getUserTimeZone)(this.state));
    }
    cellValueToStdValue(cellValue) {
        const stdVal = {
            sourceType: this.field.type,
            data: [],
        };
        if (cellValue != null) {
            /*
              * Data pasted or dragged from a date field should be saved twice
              * text is text, such as 11/02 2020-11-1, used to write data when pasting to non-date fields
              * originValue is a timestamp, which is used to paste or fill data into the date field.
              * The biggest difference from text is that all the time information is saved, which can be used according to
              * The format of the target date field is displayed freely
              */
            stdVal.data.push({
                text: this.cellValueToString(cellValue) || '',
                originValue: cellValue,
            });
        }
        return stdVal;
    }
    stdValueToCellValue(stdVal) {
        if (stdVal.data.length === 0) {
            return null;
        }
        let value = stdVal.data[0].text;
        if (!value) {
            return null;
        }
        const isOperateFromDate = stdVal.sourceType === field_types_1.FieldType.DateTime && stdVal.data[0].originValue;
        if (isOperateFromDate) {
            value = stdVal.data[0].originValue;
        }
        const _value = isOperateFromDate ? value : (0, utils_2.dateStrReplaceCN)(value);
        let datetime = (0, dayjs_1.default)(_value);
        if (datetime.isValid()) {
            /**
              * automatically fills the date with the year
              * If the data is pasted from a cell of a time field,
              * it will not be processed, but for strings pasted from text or Excel, two judgments will be made
              *
              * 1. Perform pattern matching according to the given format, check if there is a possible year,
              * if there is, use the given year
              *
              * 2. If the above conditions are not satisfied, check whether the final formatted year is 2001,
              * if it is satisfied, it will be directed to the current year
              *
              * @type {boolean}
              */
            const isIncludesYear = (0, dayjs_1.default)(_value, ['Y-M-D', 'D/M/Y']).isValid();
            if (datetime.year() === 2001 && !isIncludesYear) {
                datetime = datetime.year((0, dayjs_1.default)().year());
            }
            const timestamp = datetime.toDate().getTime();
            // When the type is switched, the illegal data is set to null
            if ((0, utils_2.notInTimestampRange)(timestamp)) {
                return null;
            }
            return timestamp;
        }
        return null;
    }
    /**
      * Assuming it is now Feb 8 01:56:55 UTC+8
      * Today: [Today 00:00, Tomorrow 23:59] UTC+8
      * Tomorrow: [Tomorrow 00:00, The day after tomorrow 23:59] UTC+8
      * Yesterday: [yesterday 00:00, today 23:59] UTC+8
      * Next 7 days: [Today 00:00, Feb 16 23:59] UTC+8
      * Last 7 days: [February 1st 00:00, today 23:59] UTC+8
      * In the next 30 days: [Today 00:00, March 9th 23:59] UTC+8
      * In the past 30 days: [January 8th 00:00, today 23:59] UTC+8
      * This week: Monday to Friday of the current week
      * Last week: Monday to Friday of the previous week
      * This month: [February 1st 00:00, February 28th 23:59] UTC+8
      * Last month: [January 1st 00:00, January 31st 23:59] UTC+8
      * This year: [January 1st 00:00, December 31st 23:59] UTC+8
      */
    static getTimeRange(filterDuration, time, timeZone) {
        switch (filterDuration) {
            case view_types_1.FilterDuration.ExactDate: {
                if (time != undefined) {
                    return [
                        withTimeZone((0, dayjs_1.default)(time), timeZone).startOf('day').valueOf(),
                        withTimeZone((0, dayjs_1.default)(time), timeZone).endOf('day').valueOf()
                    ];
                }
                throw new Error('ExactDate has to calculate with timestamp');
            }
            case view_types_1.FilterDuration.DateRange: {
                if (typeof time === 'string') {
                    const [startDate, endDate] = time.split('-');
                    return [Number(startDate), Number(endDate)];
                }
                throw new Error('ExactDate has to calculate with timestamp');
            }
            case view_types_1.FilterDuration.Today: {
                return [
                    withTimeZone((0, dayjs_1.default)(), timeZone).startOf('day').valueOf(),
                    withTimeZone((0, dayjs_1.default)(), timeZone).endOf('day').valueOf()
                ];
            }
            case view_types_1.FilterDuration.Tomorrow: {
                return [
                    withTimeZone((0, dayjs_1.default)(), timeZone).add(1, 'day').startOf('day').valueOf(),
                    withTimeZone((0, dayjs_1.default)(), timeZone).add(1, 'day').endOf('day').valueOf()
                ];
            }
            case view_types_1.FilterDuration.Yesterday: {
                return [
                    withTimeZone((0, dayjs_1.default)(), timeZone).add(-1, 'day').startOf('day').valueOf(),
                    withTimeZone((0, dayjs_1.default)(), timeZone).add(-1, 'day').endOf('day').valueOf()
                ];
            }
            case view_types_1.FilterDuration.TheNextWeek: {
                return [
                    withTimeZone((0, dayjs_1.default)(), timeZone).add(1, 'day').startOf('day').valueOf(),
                    withTimeZone((0, dayjs_1.default)(), timeZone).add(7, 'day').endOf('day').valueOf()
                ];
            }
            case view_types_1.FilterDuration.TheLastWeek: {
                return [
                    withTimeZone((0, dayjs_1.default)(), timeZone).add(-7, 'day').startOf('day').valueOf(),
                    withTimeZone((0, dayjs_1.default)(), timeZone).add(-1, 'day').endOf('day').valueOf(),
                ];
            }
            // 1/29 plus one month equals March 1st
            case view_types_1.FilterDuration.TheNextMonth: {
                return [
                    withTimeZone((0, dayjs_1.default)(), timeZone).add(1, 'day').startOf('day').valueOf(),
                    withTimeZone((0, dayjs_1.default)(), timeZone).add(30, 'day').endOf('day').valueOf()
                ];
            }
            case view_types_1.FilterDuration.TheLastMonth: {
                return [
                    withTimeZone((0, dayjs_1.default)(), timeZone).add(-30, 'day').startOf('day').valueOf(),
                    withTimeZone((0, dayjs_1.default)(), timeZone).add(-1, 'day').endOf('day').valueOf(),
                ];
            }
            case view_types_1.FilterDuration.ThisWeek: {
                return [
                    withTimeZone((0, dayjs_1.default)(), timeZone).startOf('week').valueOf(),
                    withTimeZone((0, dayjs_1.default)(), timeZone).endOf('week').valueOf()
                ];
            }
            case view_types_1.FilterDuration.PreviousWeek: {
                return [
                    withTimeZone((0, dayjs_1.default)(), timeZone).add(-1, 'week').startOf('week').valueOf(),
                    withTimeZone((0, dayjs_1.default)(), timeZone).add(-1, 'week').endOf('week').valueOf()
                ];
            }
            case view_types_1.FilterDuration.ThisMonth: {
                return [
                    withTimeZone((0, dayjs_1.default)(), timeZone).startOf('month').valueOf(),
                    withTimeZone((0, dayjs_1.default)(), timeZone).endOf('month').valueOf()
                ];
            }
            case view_types_1.FilterDuration.PreviousMonth: {
                return [
                    withTimeZone((0, dayjs_1.default)(), timeZone).add(-1, 'month').startOf('month').valueOf(),
                    withTimeZone((0, dayjs_1.default)(), timeZone).add(-1, 'month').endOf('month').valueOf()
                ];
            }
            case view_types_1.FilterDuration.ThisYear: {
                return [
                    withTimeZone((0, dayjs_1.default)(), timeZone).startOf('year').valueOf(),
                    withTimeZone((0, dayjs_1.default)(), timeZone).endOf('year').valueOf()
                ];
            }
            case view_types_1.FilterDuration.SomeDayBefore: {
                if (typeof time === 'number') {
                    return [
                        withTimeZone((0, dayjs_1.default)(), timeZone).add(-time, 'day').startOf('day').valueOf(),
                        withTimeZone((0, dayjs_1.default)(), timeZone).add(-time, 'day').endOf('day').valueOf()
                    ];
                }
                throw new Error('SomeDayBefore has to calculate with number');
            }
            case view_types_1.FilterDuration.SomeDayAfter: {
                if (typeof time === 'number') {
                    return [
                        withTimeZone((0, dayjs_1.default)(), timeZone).add(time, 'day').startOf('day').valueOf(),
                        withTimeZone((0, dayjs_1.default)(), timeZone).add(time, 'day').endOf('day').valueOf()
                    ];
                }
                throw new Error('SomeDayAfter has to calculate with number');
            }
            default: {
                (0, utils_2.assertNever)(filterDuration);
            }
        }
    }
    static _isMeetFilter(operator, cellValue, conditionValue, timeZone) {
        // The logic to judge in advance that it is empty or not.
        if (operator === view_types_1.FOperator.IsEmpty) {
            return cellValue == null;
        }
        if (operator === view_types_1.FOperator.IsNotEmpty) {
            return cellValue != null;
        }
        const [filterDuration] = conditionValue;
        let timestamp;
        if (filterDuration === view_types_1.FilterDuration.ExactDate ||
            filterDuration === view_types_1.FilterDuration.DateRange ||
            filterDuration === view_types_1.FilterDuration.SomeDayBefore ||
            filterDuration === view_types_1.FilterDuration.SomeDayAfter) {
            timestamp = conditionValue[1];
            /**
             * When there is no timestamp, it means that time selection has not been performed, and no filtering is performed at this time
             */
            if (timestamp == null) {
                return true;
            }
        }
        /**
         * When the cell value is empty, it will be hidden
         * There is no need to consider the operation condition of whether it is empty or not, this condition has been processed externally
         */
        if (cellValue == null) {
            return false;
        }
        const [left, right] = this.getTimeRange(filterDuration, timestamp, timeZone);
        switch (operator) {
            case view_types_1.FOperator.Is: {
                return left <= cellValue && cellValue < right;
            }
            case view_types_1.FOperator.IsGreater: {
                return cellValue > right;
            }
            case view_types_1.FOperator.IsGreaterEqual: {
                return cellValue >= left;
            }
            case view_types_1.FOperator.IsLess: {
                return cellValue < left;
            }
            case view_types_1.FOperator.IsLessEqual: {
                return cellValue <= right;
            }
            default: {
                return false;
                // return this.isMeetFilter(operator, cellValue, conditionValue);
            }
        }
    }
    isMeetFilter(operator, cellValue, conditionValue) {
        let timeZone = (0, selectors_1.getUserTimeZone)(this.state);
        if ((0, env_1.isServer)()) {
            timeZone = timeZone || model_1.DEFAULT_TIME_ZONE;
        }
        return DateTimeBaseField._isMeetFilter(operator, cellValue, conditionValue, timeZone);
    }
    static _statType2text(type) {
        if (type === stat_1.StatType.Max) {
            return (0, i18n_1.t)(i18n_1.Strings.stat_max_date);
        }
        if (type === stat_1.StatType.Min) {
            return (0, i18n_1.t)(i18n_1.Strings.stat_min_date);
        }
        return stat_1.StatTranslate[type];
    }
    /**
     * @description convert the statistical parameters into Chinese
     */
    statType2text(type) {
        return DateTimeBaseField._statType2text(type);
    }
    cellValueToApiStandardValue(cellValue) {
        return cellValue;
    }
    cellValueToApiStringValue(cellValue) {
        return cellValue ? this.cellValueToString(cellValue) : null;
    }
    cellValueToOpenValue(cellValue) {
        return cellValue ? this.cellValueToString(cellValue) : null;
    }
    openWriteValueToCellValue(openWriteValue) {
        return (0, utils_1.isNullValue)(openWriteValue) ? null : new Date(openWriteValue).getTime();
    }
    static _filterValueToOpenFilterValue(value) {
        if (!value || !Array.isArray(value)) {
            return null;
        }
        const operator = value[0];
        const _value = value[1];
        if (operator === view_types_1.FilterDuration.DateRange) {
            const range = typeof _value === 'string' ? _value.split('-').map(v => Number(v)) : [];
            return range.length === 2 ? [operator, range[0], range[1]] : [operator, null];
        }
        if (operator === view_types_1.FilterDuration.ExactDate) {
            return [operator, _value];
        }
        if (operator === view_types_1.FilterDuration.SomeDayBefore || operator === view_types_1.FilterDuration.SomeDayAfter) {
            return [operator, _value == null ? null : Number(_value)];
        }
        return [operator];
    }
    filterValueToOpenFilterValue(value) {
        return DateTimeBaseField._filterValueToOpenFilterValue(value);
    }
    static _openFilterValueToFilterValue(value) {
        if (!value || !Array.isArray(value)) {
            return null;
        }
        const [operator, ..._value] = value;
        if (operator === view_types_1.FilterDuration.DateRange) {
            return _value.length === 2 ? [operator, _value.join('-')] : [operator, null];
        }
        if (operator === view_types_1.FilterDuration.ExactDate) {
            return [operator, _value.length === 1 ? _value[0] : null];
        }
        if (operator === view_types_1.FilterDuration.SomeDayBefore || operator === view_types_1.FilterDuration.SomeDayAfter) {
            return [operator, _value.length === 1 ? _value[0] : null];
        }
        return [operator];
    }
    openFilterValueToFilterValue(value) {
        return DateTimeBaseField._openFilterValueToFilterValue(value);
    }
    static _validateOpenFilterValue(value) {
        if (!value) {
            return joi_1.default.allow(null).validate(value);
        }
        const [op, ..._value] = value;
        if (op === view_types_1.FilterDuration.DateRange) {
            return joi_1.default.array().items(joi_1.default.number().allow(null)).min(2).max(2).allow(null).validate(_value);
        }
        if (op === view_types_1.FilterDuration.ExactDate) {
            return joi_1.default.array().items(joi_1.default.number().allow(null)).max(1).allow(null).validate(_value);
        }
        if (op === view_types_1.FilterDuration.SomeDayBefore || op === view_types_1.FilterDuration.SomeDayAfter) {
            return joi_1.default.array().items(joi_1.default.number().allow(null)).max(1).allow(null).validate(_value);
        }
        return joi_1.default.array().max(0).validate(_value);
    }
    validateOpenFilterValue(value) {
        return DateTimeBaseField._validateOpenFilterValue(value);
    }
}
exports.DateTimeBaseField = DateTimeBaseField;
DateTimeBaseField._statTypeList = [
    stat_1.StatType.None,
    stat_1.StatType.CountAll,
    stat_1.StatType.Empty,
    stat_1.StatType.Filled,
    stat_1.StatType.Unique,
    stat_1.StatType.PercentEmpty,
    stat_1.StatType.PercentFilled,
    stat_1.StatType.PercentUnique,
    stat_1.StatType.Min,
    stat_1.StatType.Max,
    stat_1.StatType.DateRangeOfDays,
    stat_1.StatType.DateRangeOfMonths,
];
DateTimeBaseField._acceptFilterOperators = [
    view_types_1.FOperator.Is,
    view_types_1.FOperator.IsGreater,
    view_types_1.FOperator.IsGreaterEqual,
    view_types_1.FOperator.IsLess,
    view_types_1.FOperator.IsLessEqual,
    view_types_1.FOperator.IsEmpty,
    view_types_1.FOperator.IsNotEmpty,
    view_types_1.FOperator.IsRepeat,
    view_types_1.FOperator.IsMax
];
DateTimeBaseField.FOperatorDescMap = {
    [view_types_1.FOperator.Is]: (0, i18n_1.t)(i18n_1.Strings.equal),
    [view_types_1.FOperator.IsNot]: (0, i18n_1.t)(i18n_1.Strings.not_equal),
    [view_types_1.FOperator.IsGreater]: (0, i18n_1.t)(i18n_1.Strings.function_date_time_after),
    [view_types_1.FOperator.IsGreaterEqual]: (0, i18n_1.t)(i18n_1.Strings.date_after_or_equal),
    [view_types_1.FOperator.IsLess]: (0, i18n_1.t)(i18n_1.Strings.function_date_time_before),
    [view_types_1.FOperator.IsLessEqual]: (0, i18n_1.t)(i18n_1.Strings.date_before_or_equal),
    [view_types_1.FOperator.IsEmpty]: (0, i18n_1.t)(i18n_1.Strings.is_empty),
    [view_types_1.FOperator.IsNotEmpty]: (0, i18n_1.t)(i18n_1.Strings.is_not_empty),
    [view_types_1.FOperator.IsRepeat]: (0, i18n_1.t)(i18n_1.Strings.is_repeat),
    [view_types_1.FOperator.IsMax]: (0, i18n_1.t)(i18n_1.Strings.is_max)
};
//# sourceMappingURL=date_time_base_field.js.map